import { motion } from "framer-motion";
import {
  Github,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Download,
  ArrowUpRight,
} from "lucide-react";
import { profile } from "../data/portfolio";
import { SectionHeader } from "./SectionHeader";

const channels = [
  {
    testId: "contact-email-link",
    href: `mailto:${profile.email}`,
    label: "Email",
    value: profile.email,
    icon: Mail,
    external: false,
  },
  {
    testId: "contact-phone-link",
    href: `tel:${(profile.phone || "").replace(/\s+/g, "")}`,
    label: "Phone",
    value: profile.phone,
    icon: Phone,
    external: false,
  },
  {
    testId: "contact-linkedin-link",
    href: profile.linkedin,
    label: "LinkedIn",
    value: "riya-gupta",
    icon: Linkedin,
    external: true,
  },
  {
    testId: "contact-github-link",
    href: profile.github,
    label: "GitHub",
    value: "@riyagupta1106",
    icon: Github,
    external: true,
  },
];

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="relative py-24 md:py-32 px-5 sm:px-8"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          testId="contact-header"
          index="05 // contact"
          title="Let's build something"
          sub="Open to internships, collaborations and interesting AI/ML problems — the fastest way to reach me is a quick email or a message on LinkedIn."
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
          {/* Left — headline card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0d0620]/60 p-6 sm:p-8 flex flex-col justify-between"
            data-testid="contact-headline-card"
          >
            <div>
              <div className="font-mono text-[11px] tracking-widest text-purple-400 uppercase mb-4">
                Say hello
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                Currently open to
                <br />
                <span className="text-purple-300">AI/ML & Gen-AI</span> roles.
              </h3>
              <p className="mt-5 text-white/65 leading-relaxed">
                Whether it&apos;s a Gen-AI internship, a research collab, or you just want to
                talk about RAG pipelines — the inbox is always open. Response time is
                usually under 24 hours.
              </p>
            </div>

            <div className="mt-8 flex items-center gap-3 text-sm text-white/50 font-mono">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={14} className="text-purple-400" />
                {profile.location}
              </span>
            </div>
          </motion.div>

          {/* Right — channels */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {channels.map((c, i) => {
              const Icon = c.icon;
              const anchorProps = c.external
                ? { target: "_blank", rel: "noreferrer" }
                : {};
              return (
                <motion.a
                  key={c.label}
                  data-testid={c.testId}
                  href={c.href}
                  {...anchorProps}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="glow-card group rounded-xl border border-white/10 bg-[#0d0620]/60 p-6 flex flex-col justify-between min-h-[150px]"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-11 h-11 rounded-md bg-purple-500/15 border border-purple-500/25 flex items-center justify-center">
                      <Icon size={18} className="text-purple-300" />
                    </div>
                    <ArrowUpRight
                      size={18}
                      className="text-white/30 transition-all group-hover:text-purple-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </div>
                  <div>
                    <div className="font-mono text-[11px] tracking-widest text-purple-400/80 uppercase">
                      {c.label}
                    </div>
                    <div className="mt-1 text-white font-medium text-base sm:text-lg break-all">
                      {c.value}
                    </div>
                  </div>
                </motion.a>
              );
            })}

            {/* Resume CTA card */}
            <motion.a
              data-testid="contact-resume-btn"
              href={profile.resumeUrl}
              target="_blank"
              rel="noreferrer"
              download="Riya-Gupta-Resume.pdf"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: channels.length * 0.06 }}
              className="sm:col-span-2 group rounded-xl border border-purple-500/40 bg-gradient-to-br from-purple-600/20 to-purple-900/10 p-6 flex items-center justify-between hover:border-purple-400 transition-colors"
            >
              <div>
                <div className="font-mono text-[11px] tracking-widest text-purple-300 uppercase">
                  Résumé
                </div>
                <div className="mt-1 text-white font-semibold text-lg">
                  Prefer the one-pager? Download my resume.
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-purple-600 group-hover:bg-purple-500 text-white font-medium transition-colors">
                <Download size={16} /> PDF
              </div>
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
};
